import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
  FormControl,
  Switch,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { PutProductStoreRequest } from '@/services/request/product-store';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { CurrencyInputField } from '@/components/CurrencyInputField';
import { usePutProductStoreMutation } from '@/services/queries/product-store';
import { Partner, ProductStore, ProductStoreStatus } from '@/model';
import { SelectField } from '@/components/SelectField';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface ProductStoreEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  partners: Partner[];
  item: ProductStore;
  onClose: () => void;
  onDone?: () => void;
}

export const ProductStoreEditModal = ({ isOpen, onClose, onDone, item, partners }: ProductStoreEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const putItemMutation = usePutProductStoreMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team_uuid,
      title: item.title,
      file: undefined,
      price: `${item.price}`,
      source_url: item.source_url,
      publish_at: item.published_at || '',
      is_published: item.status === ProductStoreStatus.PUBLISHED,
      is_scheduled: item.status === ProductStoreStatus.SCHEDULED,
      has_partner: !!item.partner_uuid,
      partner_uuid: item.partner_uuid,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formValues = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const price = String(data.price).replace(',', '').replace('.', '');

      const payload: PutProductStoreRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.file,
        title: data.title,
        price: parseFloat(price),
        source_url: data.source_url,
        is_published: data.is_scheduled && data.publish_at ? false : !!data.is_published,
        publish_at: data.is_scheduled ? data.publish_at || '' : '',
        partner_uuid: data.partner_uuid || '',
        unpublish_at: '',
      };

      putItemMutation.mutate(
        { uuid: item.uuid, payload },
        {
          onSuccess: () => {
            onDone?.();
            reset();
            toast({
              status: 'success',
              title: t('pages.application_store.components.product_store_edit_modal.product_store_edit_update_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_store.components.product_store_edit_modal.product_store_edit_update_error'),
            });
          },
        },
      );
    },
    [item.uuid, onDone, reset, toast, putItemMutation, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_store.components.product_store_edit_modal.product_store_edit_title')}
            </Text>
            <FormControl w='fit-content' display='flex' gap={2} alignItems='center'>
              <Text fontWeight='medium' fontSize='14px' color={!formValues.is_published ? 'gray.400' : 'inherit'}>
                {t('pages.application_store.components.product_store_edit_modal.product_store_edit_published_label')}
              </Text>
              <Switch
                disabled={!!formValues.is_scheduled}
                name='is_published'
                isChecked={formValues.is_scheduled ? false : formValues.is_published}
                onChange={event => setValue('is_published', event.target.checked)}
              />
            </FormControl>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_store.components.product_store_edit_modal.product_store_edit_product_team')}
              placeholder={t('pages.application_store.components.product_store_edit_modal.product_store_edit_product_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
              <InputGroup display='flex' flexDirection='column'>
                <InputField
                  withoutFloating
                  label={t('pages.application_store.components.product_store_edit_modal.product_store_edit_product_label')}
                  {...register('title')}
                  helperText={t(
                    'pages.application_store.components.product_store_edit_modal.product_store_edit_product_helper_text',
                  )}
                  maxLength={100}
                  rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
                />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <CurrencyInputField
                  withoutFloating
                  value={formValues.price}
                  label={t('pages.application_store.components.product_store_edit_modal.product_store_edit_price_label')}
                  {...useDebouncedFieldRegistry(() => register('price'))}
                  rootProps={{ isInvalid: !!errors?.price, errorMessage: errors?.price?.message }}
                />
              </InputGroup>
            </Flex>

            <InputField
              withoutFloating
              label={t('pages.application_store.components.product_store_edit_modal.product_store_edit_link_label')}
              {...register('source_url')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.source_url, errorMessage: errors?.source_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_store.components.product_store_edit_modal.product_store_edit_photo_label')}
                imageURL={item.image_url}
                onFileChange={file => setValue('file', file)}
                helpText={t('pages.application_store.components.product_store_edit_modal.product_store_edit_photo_helper_text')}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>

            {formValues.is_scheduled && (
              <InputField
                withoutFloating
                label={t('pages.application_store.components.product_store_edit_modal.product_store_edit_date_label')}
                type='datetime-local'
                fullDatePickerArea
                min={formatDateToDateTimeLocalInputValue(new Date())}
                {...register('publish_at')}
                rootProps={{ isInvalid: !!errors?.publish_at, errorMessage: errors?.publish_at?.message }}
              />
            )}

            <FormControl display='flex' gap={2} alignItems='center'>
              <Text fontWeight='medium'>
                {t('pages.application_store.components.product_store_edit_modal.product_store_edit_partner_label')}
              </Text>
              <Switch
                name='has_partner'
                isChecked={formValues.has_partner}
                onChange={event => {
                  setValue('has_partner', event.target.checked);
                }}
              />
            </FormControl>

            {formValues.has_partner && (
              <InputGroup display='flex' flexDirection='column' mb={4}>
                <Text fontWeight='medium'>
                  {t('pages.application_store.components.product_store_edit_modal.product_store_edit_partner_select_label')}
                </Text>
                <SelectField
                  {...register('partner_uuid')}
                  rootProps={{
                    isInvalid: !!errors?.partner_uuid,
                    errorMessage: errors?.partner_uuid?.message?.toString(),
                    mt: 0,
                  }}
                >
                  <option value=''>
                    {t('pages.application_store.components.product_store_edit_modal.product_store_edit_partner_select_all')}
                  </option>
                  {partners?.map(partner => (
                    <option key={partner.uuid} value={partner.uuid}>
                      {partner.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_store.components.product_store_edit_modal.product_store_edit_cancel')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_store.components.product_store_edit_modal.product_store_edit_save_changes')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
